import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { Toast } from '../proposition-c/ui';
import { CreateWizard } from './CreateWizard';
import { HomePage } from './HomePage';
import { useProjects, useReferential } from './hooks';
import { ProjectView } from './ProjectView';
import { STUDIO_HEADER, UMR } from './tokens';

export const meta = {
  title: "Création d'un projet",
  description:
    'Outil Plan repensé : liste des projets, création en 3 étapes (nom + périmètre communes ou dessiné, ratios de consommation en tableau, DJU) avec impact live sur la consommation globale, carte des consommations et synchronisation manuelle avec UrbanMap.',
  tags: ['wizard', 'planning', 'map'],
  author: 'plan-team',
  status: 'draft' as const,
  createdAt: '2026-10-16',
  updatedAt: '2026-10-20',
};

type View = { kind: 'home'; wizard: boolean } | { kind: 'project'; id: string };

/** `?projets=0` dans l'URL affiche l'état vide de l'accueil. */
const emptyHome = () => new URLSearchParams(window.location.search).get('projets') === '0';

export default function PlanProjetChaleur() {
  const qc = useQueryClient();
  const [empty] = useState(emptyHome);
  const projects = useProjects(empty);
  const referential = useReferential();
  const [view, setView] = useState<View>({ kind: 'home', wizard: false });
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (document.querySelector(`link[href="${UMR.fontHref}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = UMR.fontHref;
    document.head.appendChild(link);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const goHome = () => {
    setView({ kind: 'home', wizard: false });
    void qc.invalidateQueries({ queryKey: ['plan2cost', 'projet-chaleur', 'projects'] });
  };

  let body: JSX.Element;
  if (view.kind === 'project') {
    body = referential.data
      ? <ProjectView key={view.id} projectId={view.id} referential={referential.data} onBack={goHome} flash={flash} />
      : <div style={{ padding: 24 }}>{referential.isError ? 'Erreur de chargement du référentiel.' : 'Chargement…'}</div>;
  } else {
    body = (
      <div style={{ position: 'relative', height: '100%' }}>
        <HomePage
          projects={projects.data}
          loading={projects.isLoading}
          error={projects.isError}
          onCreate={() => setView({ kind: 'home', wizard: true })}
          onOpen={(id) => setView({ kind: 'project', id })}
        />
        {view.wizard && referential.data && (
          <CreateWizard
            referential={referential.data}
            onCancel={() => setView({ kind: 'home', wizard: false })}
            onOpen={(id) => {
              setView({ kind: 'project', id });
              flash('Projet créé · copie des bâtiments UrbanMap figée à aujourd’hui');
            }}
          />
        )}
      </div>
    );
  }

  return (
    <PrototypeStudioLayout title={meta.title}>
      <div style={{ position: 'relative', height: `calc(100vh - ${STUDIO_HEADER}px)`, overflow: 'hidden', fontFamily: UMR.font, background: UMR.white, color: UMR.textStrong }}>
        {body}
        {toast && <Toast>{toast}</Toast>}
      </div>
    </PrototypeStudioLayout>
  );
}
